import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 * @typedef {object} DrawerState
 * @property {boolean} open - The latest intent, including while a close transition is running.
 * @property {number} epoch
 * @property {number | null} frame
 * @property {ReturnType<typeof setTimeout> | null} timer
 * @property {HTMLElement[]} accordionContent
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {Map<HTMLDetailsElement, DrawerState>} */
  #states = new Map();
  /** @type {HTMLDetailsElement | null} */
  #focusDetails = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    this.removeEventListener('keyup', this.#onKeyUp);
    this.querySelectorAll('.menu-drawer__animated-element').forEach((element) => {
      element.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
    this.#states.forEach((state, details) => {
      this.#cancelPending(state);
      reset(details);
    });
    this.#states.clear();
    this.refs.menuDrawer?.classList.remove('menu-drawer--has-submenu-opened');
    if (this.#focusDetails) removeTrapFocus();
    this.#focusDetails = null;
    super.disconnectedCallback();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.#state(this.refs.details).open;
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    event?.preventDefault();
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary || !this.isConnected || (details !== this.refs.details && !this.isOpen)) return;

    const state = this.#state(details);
    if (state.open) return;
    const epoch = this.#begin(state, true);
    this.#invalidateDescendants(details, true);
    if (details === this.refs.details) {
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    }

    // Reset while the details subtree is renderable. Hidden <details> can retain
    // a finished CSSAnimation until a style boundary actually observes `none`.
    details.classList.remove('menu-open');
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(details);
    details.querySelectorAll('.menu-drawer__animated-element').forEach((element) => {
      getComputedStyle(element).animationName;
    });
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu') || details;
    drawer.getBoundingClientRect();

    state.frame = requestAnimationFrame(() => {
      state.frame = null;
      if (!this.#current(details, state, epoch) || !state.open) return;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      onAnimationEnd(drawer, () => {
        if (this.#current(details, state, epoch) && state.open && !this.#hasOpenDescendant(details)) {
          // The classic panel covers the outer menu summary. Keep its focus
          // cycle inside the visible panel rather than that obscured trigger.
          const focusRoot = details === this.refs.details && this.refs.menuDrawer.classList.contains('menu-drawer--classic')
            ? this.refs.menuDrawer : details;
          trapFocus(focusRoot, { visibleOnly: focusRoot === this.refs.menuDrawer });
          if (focusRoot === this.refs.menuDrawer) {
            focusRoot.querySelector('.menu-drawer__close-button')?.focus({ preventScroll: true });
          }
          this.#focusDetails = details;
        }
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Accordion summaries toggle native [open] without calling this drawer.
    // Do not cache their intent, reset submenus, or replace their enclosing trap.
    if (details.parentElement?.localName === 'accordion-custom') {
      if (!details.hasAttribute('open')) return;
      summary.focus({ preventScroll: true });
      details.removeAttribute('open');
      return;
    }

    const state = this.#state(details);
    if (!state.open) return;
    const epoch = this.#begin(state, false);
    this.#invalidateDescendants(details, false);

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (!this.#current(details, state, epoch) || state.open) return;
        reset(details);
        this.#invalidateDescendants(details, true);
        if (details === this.refs.details) {
          if (this.#focusDetails) removeTrapFocus();
          this.#focusDetails = null;
          if (summary.isConnected && summary.getClientRects().length > 0) {
            state.frame = requestAnimationFrame(() => {
              state.frame = null;
              if (this.#current(details, state, epoch) && !state.open) summary.focus({ preventScroll: true });
            });
          }
        } else if (this.isOpen && !this.#hasOpenDescendant(this.refs.details)) {
          const classic = this.refs.menuDrawer.classList.contains('menu-drawer--classic');
          trapFocus(classic ? this.refs.menuDrawer : this.refs.details, { visibleOnly: classic });
          this.#focusDetails = this.refs.details;
        }
      },
      { subtree: false }
    );
  }

  /** @param {HTMLDetailsElement} details @returns {DrawerState} */
  #state(details) {
    let state = this.#states.get(details);
    if (!state) {
      state = { open: details.hasAttribute('open'), epoch: 0, frame: null, timer: null, accordionContent: [] };
      this.#states.set(details, state);
    }
    return state;
  }

  /** @param {DrawerState} state @param {boolean} open */
  #begin(state, open) {
    this.#cancelPending(state);
    state.open = open;
    return ++state.epoch;
  }

  /** @param {DrawerState} state */
  #cancelPending(state) {
    if (state.frame !== null) cancelAnimationFrame(state.frame);
    state.frame = null;
    this.#clearAccordion(state);
  }

  /** @param {DrawerState} state */
  #clearAccordion(state) {
    if (state.timer !== null) clearTimeout(state.timer);
    state.timer = null;
    state.accordionContent.forEach((element) => element.classList.remove('details-content--no-animation'));
    state.accordionContent = [];
  }

  /** @param {HTMLDetailsElement} details @param {DrawerState} state @param {number} epoch */
  #current(details, state, epoch) {
    return this.isConnected && details.isConnected && this.#states.get(details) === state && state.epoch === epoch;
  }

  /** @param {HTMLDetailsElement} details @param {boolean} resetNow */
  #invalidateDescendants(details, resetNow) {
    details.querySelectorAll('details:not(accordion-custom > details)').forEach((child) => {
      if (!(child instanceof HTMLDetailsElement)) return;
      this.#begin(this.#state(child), false);
      if (resetNow) reset(child);
    });
  }

  /** @param {HTMLDetailsElement} details */
  #hasOpenDescendant(details) {
    return [...this.#states].some(([child, state]) => child !== details && details.contains(child) && state.open);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const state = this.#state(details);
    const epoch = state.epoch;
    this.#clearAccordion(state);
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
        state.accordionContent.push(element);
      }
    });
    state.timer = setTimeout(() => {
      if (this.#current(details, state, epoch)) this.#clearAccordion(state);
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
